import { Button, cn } from "@tellescope/ui";
import { Button as ActionButton } from "@tellescope/design-system";
import { ComponentProps, ReactNode } from "react";
import { useNavbarShallowStore } from "../store";
import { LucideIcon, PanelLeft, X } from "lucide-react";
import { usePageLabel } from "../hooks";
import { useTranslation } from "react-i18next";
import { PAGE_HEADER_HEIGHT_CLASSES } from "../constants/header-sizing";

export interface PageHeaderAction
  extends Omit<ComponentProps<typeof ActionButton>, "onClick" | "children" | "iconOnly"> {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}

export interface PageHeaderProps {
  children?: ReactNode;
  onClose?: () => void;
  className?: string;
  hideTitle?: boolean;
  trailingActions?: PageHeaderAction[];
}

export const PageHeader = ({ children, onClose, className, hideTitle = false, trailingActions }: PageHeaderProps) => {
  const { t } = useTranslation();
  const { isCollapsed, toggleCollapsed } = useNavbarShallowStore("isCollapsed", "toggleCollapsed");
  const pageTitle = usePageLabel(t("Tellescope"));

  return (
    <div
      className={cn(
        "flex items-center px-2 border-b justify-between w-full flex-shrink-0",
        PAGE_HEADER_HEIGHT_CLASSES,
        className
      )}
    >
      <div className="flex items-center gap-2 w-full">
        <Button
          variant="ghost"
          size="icon"
          className="w-9 h-9 rounded-md"
          onClick={toggleCollapsed}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <PanelLeft className="h-5 w-5" />
        </Button>
        {hideTitle ? null : typeof children === "string" || typeof children === "undefined" ? (
          <h1 className="text-xl font-bold truncate">{children ?? pageTitle}</h1>
        ) : (
          children
        )}
      </div>
      {(!!trailingActions?.length || !!onClose) && (
        <div className="flex items-center gap-1">
          {trailingActions?.map(({ icon: Icon, label, onClick, ...rest }) => (
            <ActionButton
              iconOnly
              variant="ghost"
              size="sm"
              color="muted"
              hoverLabel={label}
              onClick={onClick}
              {...rest}
              key={label}
            >
              <Icon />
            </ActionButton>
          ))}
          {!!onClose && (
            <Button variant="ghost" size="sm-icon" onClick={onClose}>
              <X />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
